import { Checkout } from '@df/checkout/checkout.interfaces';
import { Customer } from '@df/session/customer/customer.interfaces';

declare global {
    interface Window {
        paypal?: {
            Button?: Paypal.IButton;
            Buttons?: Paypal.ISDKButtons;
        };
    }
}
export namespace Paypal {
    export enum SCRIPTS {
        CHECKOUT = 'https://www.paypalobjects.com/api/checkout.js'
    }

    export interface IButton {
        render(configuration: IButtonRenderConfiguration, selector: string): void;
    }

    export interface IButtonRenderConfiguration {
        // 'production' is default
        env?: ButtonEnv;
        // 'en_US' is default
        locale?: ButtonLocale;
        style?: IButtonStyle;
        // Show a 'Pay Now' button
        commit?: boolean;
        payment: ButtonRenderPayment;
        onAuthorize: ButtonRenderOnAuthorize;
        onCancel: ButtonRenderOnCancel;
        onError: ButtonRenderOnError;
    }

    export type ButtonRenderPayment = (data?, actions?) => any;
    export type ButtonRenderOnAuthorize = (data?, actions?) => any;
    export type ButtonRenderOnCancel = (data?, actions?) => any;
    export type ButtonRenderOnError = (err?) => any;

    export interface IButtonStyle {
        shape?: ButtonStyleShape;
        size?: ButtonStyleSize;
        // 25 - 55
        height: number;
        color?: ButtonStyleColor;
        label?: ButtonStyleLabel;
        layout?: ButtonLayout;
        // To disable the tagline/text beneath the button
        tagline?: boolean;
    }

    export type ButtonStyleColor = 'gold' | 'blue' | 'silver' | 'black';

    export type ButtonStyleShape = 'pill' | 'rect';

    export type ButtonStyleSize = 'small' | 'medium' | 'large' | 'responsive';

    export type ButtonLayout = 'vertical' | 'horizontal';

    // checkout -	The PayPal Checkout button. The default button.
    // credit   -	The PayPal Credit button. Initializes the credit flow. Cannot be used with any custom color option.
    // pay      -	The Pay With PayPal button. Initializes the checkout flow.
    // buynow   -	The Buy Now button. Initializes the checkout flow. The default Buy Now button is unbranded.
    // To include PayPal branding, set branding: true.
    // paypal   -	The generic PayPal button. Initializes the checkout flow. This button contains only the PayPal brand logo.
    export type ButtonStyleLabel = 'checkout' | 'credit' | 'pay' | 'buynow' | 'paypal';

    export type ButtonEnv = 'sandbox' | 'production';

    export type ButtonLocale =
        | 'en_US'
        | 'en_AU'
        | 'en_GB'
        | 'fr_CA'
        | 'es_ES'
        | 'it_IT'
        | 'fr_FR'
        | 'de_DE'
        | 'pt_BR'
        | 'zh_CN'
        | 'da_DK'
        | 'zh_HK'
        | 'id_ID'
        | 'he_IL'
        | 'ja_JP'
        | 'ko_KR'
        | 'nl_NL'
        | 'no_NO'
        | 'pl_PL'
        | 'pt_PT'
        | 'ru_RU'
        | 'sv_SE'
        | 'th_TH'
        | 'zh_TW';

    export interface IPayee {
        merchant_id: string;
    }

    export interface IActions {
        enable(): void;
        disable(): void;
    }

    /////////////////////////////////////
    ////// PAYPAL SDK
    /////////////////////////////////////

    export type ISDKButtons = (configuration: ISDKButtonsConfiguration) => ISDKButton;

    export interface ISDKButtonsConfiguration {
        style?: ISDKButtonStyles;
        createOrder?: () => Promise<string | undefined>; //should return token from BE;
        onInit?: () => void;
        onApprove?: (data: IExpressOnApproveData) => void;
        onClick?: (data: ISDKButtonOnClickResult) => void;
        onCancel?: () => void;
        onError?: (err?: any) => void;
    }

    export interface ISDKButton {
        render(selector: string): void;
    }

    export interface ISDKButtonOnClickResult {
        fundingSource: string;
    }

    export interface ISDKButtonConfig {
        styles?: ISDKButtonStyles;
        isVisibleOnProductPage?: boolean;
        isGuestCheckoutAllowed?: boolean;
    }

    export interface ISDKButtonStyles {
        layout?: string;
        size?: string;
        color?: string;
        shape?: string;
        label?: string;
    }

    /////////////////////////////////////
    ////// PAYPAL EXPRESS
    /////////////////////////////////////

    export interface IExpressConfig {
        // path for review page, needed for paypal express to work
        // should be inside checkout, eg. checkout/paypal-express-review
        review_path: string;
    }

    export enum EXPRESS_ENDPOINTS {
        buttonConfig = 'paypal_button/config',
        tokenData = 'paypal_button/token',
        authorization = 'paypal_button/onAuthorization',
        reviewData = 'paypal_button/review',
        saveShippingMethod = 'paypal_button/saveShippingMethod',
        submitOrder = 'paypal_button/placeOrder'
    }

    export interface IExpressTokenResult {
        token: string;
    }


    export interface IExpressOnApproveData {
        orderID: string;
        payerID: string;
    }

    export interface IExpressAuthorisationData {
        paymentToken: string;
        payerID: string;
        paypalFundingSource?: string;
    }

    export interface IExpressReviewData extends Customer.ILoadResponse {
        quote: IExpressReviewDataQuote;
        shipping_methods: Checkout.IShippingMethod[];
        token: string;
    }

    export interface IExpressReviewDataQuote {
        email: string;
        is_virtual: boolean;
        address: {
            billing: Checkout.IBillingAddress;
            shipping: Checkout.IAddress;
        };
        shipping_method?: string;
    }
}
